import React, { useState, useEffect } from 'react';
import {
  X,
  UserCheck,
  MapPin,
  Phone,
  Clock,
  CheckCircle2,
  Navigation,
  KeyRound,
  AlertCircle,
  ChevronRight,
  LogOut,
  Power,
  RefreshCw,
  Download,
  Smartphone
} from 'lucide-react';
import { usePWAInstall } from '../utils/pwaInstall';
import { StaffInstallGuideModal } from './StaffInstallGuideModal';
import {
  StaffPartner,
  getStaffPartners,
  fetchPublicPartners,
  authenticateStaffRemote,
  authenticateStaff,
  toggleStaffDuty,
  getPartnerJobs,
  fetchPartnerJobsRemote,
  claimJob,
  updateJobTransitStatus,
  verifyStartOtpAndBegin,
  completeJobAndCollectPayment
} from '../services/partnerService';
import { BookingRecord } from '../utils/excelExport';

interface PartnerPortalModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PartnerPortalModal: React.FC<PartnerPortalModalProps> = ({
  isOpen,
  onClose
}) => {
  // Staff Auth State
  const [activeStaff, setActiveStaff] = useState<StaffPartner | null>(null);
  const [staffList, setStaffList] = useState<StaffPartner[]>(() => getStaffPartners());
  const [selectedStaffId, setSelectedStaffId] = useState<string>(() => {
    const list = getStaffPartners();
    return list[0]?.id || '';
  });
  const [pinInput, setPinInput] = useState<string>('1234');
  const [authError, setAuthError] = useState<string>('');
  const [isLoggingIn, setIsLoggingIn] = useState<boolean>(false);

  // Portal Tab State
  const [activeTab, setActiveTab] = useState<'active' | 'leads' | 'completed'>('active');

  // Partner Jobs State
  const [assignedJobs, setAssignedJobs] = useState<BookingRecord[]>([]);
  const [availableLeads, setAvailableLeads] = useState<BookingRecord[]>([]);
  const [completedJobs, setCompletedJobs] = useState<BookingRecord[]>([]);

  // Doorstep Start OTP Modal State
  const [otpTargetRef, setOtpTargetRef] = useState<string | null>(null);
  const [otpInputValue, setOtpInputValue] = useState<string>('');
  const [otpVerifyError, setOtpVerifyError] = useState<string>('');

  // Payment Collection & Complete Modal State
  const [paymentTargetBooking, setPaymentTargetBooking] = useState<BookingRecord | null>(null);
  const [selectedPaymentMode, setSelectedPaymentMode] = useState<'Cash' | 'UPI'>('Cash');

  // PWA Staff App Install State
  const { isStandalone } = usePWAInstall();
  const [showStaffInstallModal, setShowStaffInstallModal] = useState<boolean>(false);

  // Sync staff roster from Cloudflare D1 and load persisted staff login
  useEffect(() => {
    if (isOpen) {
      localStorage.setItem('blishka_installed_portal', 'staff');
      // 1. Fetch fresh roster from Cloudflare D1
      fetchPublicPartners().then((fresh) => {
        if (fresh && fresh.length > 0) {
          setStaffList(fresh);
          setSelectedStaffId((prev) => {
            if (!prev || !fresh.some((s) => s.id === prev)) {
              return fresh[0]?.id || '';
            }
            return prev;
          });
        }
      });

      // 2. Check saved session
      const savedStaffId = sessionStorage.getItem('blishka_partner_staff_id');
      if (savedStaffId) {
        const currentList = getStaffPartners();
        const staff = currentList.find((s) => s.id === savedStaffId);
        if (staff) {
          setActiveStaff(staff);
          loadJobs(staff.id);
        }
      }
    }
  }, [isOpen]);

  // Prevent background scroll when portal modal is open on mobile / desktop
  useEffect(() => {
    if (isOpen) {
      const prevOverflow = document.body.style.overflow;
      const prevTouchAction = document.body.style.touchAction;
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'pan-y';
      return () => {
        document.body.style.overflow = prevOverflow;
        document.body.style.touchAction = prevTouchAction;
      };
    }
  }, [isOpen]);

  // Loading indicator for remote job sync
  const [isSyncing, setIsSyncing] = useState<boolean>(false);

  const loadJobs = async (staffId: string) => {
    // 1. Instant local render from storage cache
    const local = getPartnerJobs(staffId);
    setAssignedJobs(local.assigned);
    setAvailableLeads(local.available);
    setCompletedJobs(local.completed);

    // 2. Fetch real-time fresh jobs from Cloudflare D1
    setIsSyncing(true);
    try {
      const remote = await fetchPartnerJobsRemote(staffId);
      setAssignedJobs(remote.assigned);
      setAvailableLeads(remote.available);
      setCompletedJobs(remote.completed);
    } catch (err) {
      console.warn('Failed to load remote jobs for staff member:', err);
    } finally {
      setIsSyncing(false);
    }
  };

  // Auto-refresh jobs when staff member switches to or refocuses this tab
  useEffect(() => {
    if (!isOpen || !activeStaff) return;
    const handleFocus = () => {
      loadJobs(activeStaff.id);
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [isOpen, activeStaff]);

  if (!isOpen) return null;

  // Handle Staff Login with server-side authentication
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setIsLoggingIn(true);

    const targetStaffId = selectedStaffId || staffList[0]?.id || '';

    // Try server authentication first with local fallback
    let staff = await authenticateStaffRemote(targetStaffId, pinInput);

    if (!staff && staffList.length > 0) {
      // Local fallback check
      staff = authenticateStaff(targetStaffId, pinInput, staffList);
    }

    setIsLoggingIn(false);

    if (staff) {
      setActiveStaff(staff);
      setSelectedStaffId(staff.id);
      sessionStorage.setItem('blishka_partner_staff_id', staff.id);
      loadJobs(staff.id);
    } else {
      setAuthError('Invalid PIN code. Default PIN is 1234.');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('blishka_partner_staff_id');
    setActiveStaff(null);
    setPinInput('1234');
  };

  // Toggle Duty Online/Offline
  const handleToggleDuty = () => {
    if (!activeStaff) return;
    const newStatus = !activeStaff.isOnline;
    const updated = toggleStaffDuty(activeStaff.id, newStatus);
    if (updated) {
      setActiveStaff({ ...updated });
    }
  };

  // Claim a Lead
  const handleClaim = async (refId: string) => {
    if (!activeStaff) return;
    const res = claimJob(activeStaff.id, refId);
    if (res) {
      await loadJobs(activeStaff.id);
      setActiveTab('active');
    }
  };

  // Update Transit Status
  const handleTransit = async (refId: string, status: 'in_transit' | 'arrived') => {
    if (!activeStaff) return;
    updateJobTransitStatus(refId, status);
    await loadJobs(activeStaff.id);
  };

  // Open Start OTP Modal
  const handleOpenOtpModal = (refId: string) => {
    setOtpTargetRef(refId);
    const matching = assignedJobs.find((j) => j.bookingRef === refId);
    setOtpInputValue(matching?.startOtp || '');
    setOtpVerifyError('');
  };

  // Submit Start OTP
  const handleVerifyOtp = async () => {
    if (!activeStaff || !otpTargetRef) return;
    const res = verifyStartOtpAndBegin(otpTargetRef, otpInputValue);
    if (res.success) {
      setOtpTargetRef(null);
      setOtpInputValue('');
      await loadJobs(activeStaff.id);
    } else {
      setOtpVerifyError(res.error || 'Invalid 4-digit code. Check with customer.');
    }
  };

  // Complete Job & Record Payment
  const handleConfirmComplete = async () => {
    if (!activeStaff || !paymentTargetBooking) return;
    completeJobAndCollectPayment(activeStaff.id, paymentTargetBooking.bookingRef, selectedPaymentMode);
    setPaymentTargetBooking(null);
    await loadJobs(activeStaff.id);
    setActiveTab('completed');
  };

  return (
    <div className="portal-fullscreen-shell">
      {/* ── TOP APP BAR (Responsive Fullscreen) ── */}
      <header className="portal-header">
        <div className="portal-header-left">
          <div
            style={{
              width: '38px',
              height: '38px',
              borderRadius: '10px',
              background: 'rgba(125, 26, 56, 0.1)',
              color: 'var(--blishka-berry)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <UserCheck size={22} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.15rem', color: 'var(--blishka-berry)', fontFamily: 'var(--font-serif)' }}>
              {activeStaff ? `${activeStaff.name} · Staff Console` : 'Blishka Staff Console'}
            </h3>
            <span className="portal-header-sub">
              {activeStaff ? `${activeStaff.area} · Doorstep Service Execution` : 'Doorstep Service Execution · Service Management · Pay After Service'}
            </span>
          </div>
        </div>

        <div className="portal-header-right">
          {activeStaff && (
            <>
              <button
                type="button"
                onClick={() => loadJobs(activeStaff.id)}
                className="btn btn-outline btn-sm"
                title="Refresh Assigned Bookings from Cloudflare D1"
                disabled={isSyncing}
                style={{ gap: '0.35rem' }}
              >
                <RefreshCw size={13} style={{ animation: isSyncing ? 'spin 1s linear infinite' : 'none' }} />
                <span className="portal-btn-label-desktop">{isSyncing ? 'Syncing...' : 'Refresh'}</span>
              </button>

              <button
                type="button"
                onClick={handleToggleDuty}
                className="btn btn-sm"
                style={{
                  background: activeStaff.isOnline ? 'rgba(46, 125, 50, 0.1)' : 'rgba(125, 26, 56, 0.1)',
                  color: activeStaff.isOnline ? '#2e7d32' : 'var(--blishka-berry)',
                  border: '1px solid currentColor',
                  gap: '0.35rem'
                }}
              >
                <Power size={13} />
                <span>{activeStaff.isOnline ? '● On' : '○ Off'}<span className="portal-btn-label-desktop"> Duty</span></span>
              </button>

              <button
                type="button"
                onClick={handleLogout}
                className="btn btn-outline btn-sm"
                title="Switch Staff Profile"
                style={{ gap: '0.35rem' }}
              >
                <LogOut size={13} />
                <span className="portal-btn-label-desktop">Switch</span>
              </button>
            </>
          )}

          {/* Install Staff App button if not standalone */}
          {!isStandalone && (
            <button
              type="button"
              onClick={() => setShowStaffInstallModal(true)}
              className="btn btn-sm"
              style={{
                gap: '0.35rem',
                background: 'rgba(125, 26, 56, 0.08)',
                color: 'var(--blishka-berry)',
                border: '1px solid rgba(125, 26, 56, 0.25)',
                fontWeight: 600
              }}
              title="Install Blishka Staff App on your phone"
            >
              <Download size={13} />
              <span>Install<span className="portal-btn-label-desktop"> App</span></span>
            </button>
          )}

          {isStandalone && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.25rem',
                background: 'rgba(46, 125, 50, 0.08)',
                color: '#2e7d32',
                fontSize: '0.68rem',
                fontWeight: 700,
                padding: '0.12rem 0.45rem',
                borderRadius: '999px',
                border: '1px solid rgba(46, 125, 50, 0.2)'
              }}
              title="Running in Standalone Staff App mode"
            >
              <Smartphone size={10} />
              <span className="portal-btn-label-desktop">App Mode</span>
            </span>
          )}

          <button
            type="button"
            onClick={onClose}
            className="btn btn-outline btn-sm"
            style={{ gap: '0.35rem' }}
          >
            <X size={15} />
            <span>Exit</span>
          </button>
        </div>
      </header>

      {/* ── VIEW 1: LOGIN CARD (Centered in Fullscreen) ── */}
      {!activeStaff ? (
        <div
          className="portal-staff-scroll-body"
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1.5rem 1rem'
          }}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '20px',
              border: '1px solid var(--border-soft)',
              boxShadow: 'var(--shadow-lg)',
              maxWidth: '440px',
              width: '100%',
              padding: '2.5rem 1.75rem',
              textAlign: 'center'
            }}
          >
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'rgba(125, 26, 56, 0.1)',
                color: 'var(--blishka-berry)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.25rem'
              }}
            >
              <UserCheck size={32} />
            </div>

            <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.5rem', color: 'var(--blishka-berry)', margin: '0 0 0.35rem' }}>
              Staff Duty Login
            </h3>
            <p style={{ fontSize: '0.86rem', color: 'var(--color-text-muted)', marginBottom: '1.75rem' }}>
              Select your profile and enter your 4-digit PIN to access your duty queue.
            </p>

            <form onSubmit={handleLogin} style={{ textAlign: 'left' }}>
              <div className="form-group" style={{ marginBottom: '1.1rem' }}>
                <label className="form-label">Select Beautician</label>
                <select
                  value={selectedStaffId || (staffList[0]?.id ?? '')}
                  onChange={(e) => {
                    setSelectedStaffId(e.target.value);
                    if (authError) setAuthError('');
                  }}
                  className="form-input"
                  style={{ width: '100%', padding: '0.75rem' }}
                >
                  {staffList.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} ({s.specialty}) · {s.phone}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <label className="form-label">Enter Staff PIN (Default: 1234)</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="password"
                    maxLength={4}
                    value={pinInput}
                    onChange={(e) => {
                      setPinInput(e.target.value);
                      if (authError) setAuthError('');
                    }}
                    placeholder="4-digit PIN"
                    className="form-input"
                    style={{ width: '100%', padding: '0.75rem', letterSpacing: '6px', textAlign: 'center', fontSize: '1.25rem', fontWeight: 700 }}
                  />
                  <KeyRound size={18} style={{ position: 'absolute', right: '14px', top: '14px', color: 'var(--color-text-muted)' }} />
                </div>
              </div>

              {authError && (
                <div style={{ color: '#d32f2f', fontSize: '0.82rem', marginBottom: '1rem', textAlign: 'center' }}>
                  {authError}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoggingIn}
                className="btn btn-berry"
                style={{ width: '100%', padding: '0.85rem', fontSize: '0.95rem' }}
              >
                {isLoggingIn ? 'Verifying PIN...' : 'Access My Duty Board'}
              </button>
            </form>

            {/* Install Staff App Banner Card on Login Screen */}
            {!isStandalone && (
              <div
                style={{
                  marginTop: '1.5rem',
                  padding: '0.95rem 1rem',
                  borderRadius: '14px',
                  background: 'linear-gradient(135deg, rgba(125, 26, 56, 0.04) 0%, rgba(125, 26, 56, 0.08) 100%)',
                  border: '1px dashed rgba(125, 26, 56, 0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.75rem',
                  textAlign: 'left'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                  <div
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '10px',
                      background: 'var(--blishka-berry)',
                      color: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <Smartphone size={18} />
                  </div>
                  <div>
                    <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--blishka-berry)' }}>
                      Install Blishka Staff App
                    </div>
                    <div style={{ fontSize: '0.72rem', color: '#7c686e' }}>
                      Fast 1-tap duty access on your phone
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setShowStaffInstallModal(true)}
                  className="btn btn-berry btn-sm"
                  style={{
                    padding: '0.35rem 0.75rem',
                    fontSize: '0.76rem',
                    gap: '0.35rem',
                    flexShrink: 0,
                    borderRadius: '8px'
                  }}
                >
                  <Download size={12} />
                  <span>Install</span>
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* ── VIEW 2: FULL-SCREEN STAFF DASHBOARD ── */
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Navigation Tabs (Horizontal Scrollable on Mobile) */}
          <nav className="portal-tabs-bar">
            <button
              type="button"
              onClick={() => setActiveTab('active')}
              className="portal-tab-btn"
              style={{
                borderBottom: activeTab === 'active' ? '3px solid var(--blishka-berry)' : '3px solid transparent',
                color: activeTab === 'active' ? 'var(--blishka-berry)' : 'var(--color-text-muted)',
                fontWeight: activeTab === 'active' ? 700 : 500
              }}
            >
              Active Visits ({assignedJobs.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('leads')}
              className="portal-tab-btn"
              style={{
                borderBottom: activeTab === 'leads' ? '3px solid var(--blishka-berry)' : '3px solid transparent',
                color: activeTab === 'leads' ? 'var(--blishka-berry)' : 'var(--color-text-muted)',
                fontWeight: activeTab === 'leads' ? 700 : 500
              }}
            >
              Available Leads ({availableLeads.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('completed')}
              className="portal-tab-btn"
              style={{
                borderBottom: activeTab === 'completed' ? '3px solid var(--blishka-berry)' : '3px solid transparent',
                color: activeTab === 'completed' ? 'var(--blishka-berry)' : 'var(--color-text-muted)',
                fontWeight: activeTab === 'completed' ? 700 : 500
              }}
            >
              Completed Visits ({completedJobs.length})
            </button>
          </nav>

          {/* Scrollable Dashboard Body */}
          <div className="portal-staff-scroll-body">
            <div style={{ maxWidth: '1360px', margin: '0 auto', width: '100%' }}>
              {/* ── TAB 1: ACTIVE ASSIGNED VISITS ── */}
              {activeTab === 'active' && (
                <div>
                  {assignedJobs.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '3.5rem 1rem', color: 'var(--color-text-muted)' }}>
                      <CheckCircle2 size={44} style={{ margin: '0 auto 0.75rem', color: '#5c7c64' }} />
                      <p style={{ margin: 0, fontWeight: 600, fontSize: '1.05rem' }}>No active visits right now.</p>
                      <p style={{ fontSize: '0.84rem', marginTop: '0.35rem' }}>
                        Switch to the <strong>"Available Leads"</strong> tab to claim incoming bookings!
                      </p>
                    </div>
                  ) : (
                    assignedJobs.map((b) => {
                      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        b.address + ', ' + b.area + ', Bhilai'
                      )}`;
                      const status = b.serviceStatus || 'assigned';

                      return (
                        <div
                          key={b.bookingRef}
                          className="portal-staff-card"
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.85rem' }}>
                              <div>
                                <span style={{ fontSize: '0.74rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                  Ref ID: {b.bookingRef}
                                </span>
                                <h4 style={{ margin: '0.15rem 0', fontSize: '1.15rem', color: 'var(--color-text-main)' }}>
                                  {b.clientName}
                                </h4>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--color-text-muted)', fontSize: '0.82rem' }}>
                                  <Clock size={13} />
                                  <span>{b.preferredDate} ({b.preferredSlot})</span>
                                </div>
                              </div>

                              <span
                                style={{
                                  fontSize: '0.76rem',
                                  padding: '0.25rem 0.75rem',
                                  borderRadius: '999px',
                                  background: status === 'in_progress' ? 'rgba(216, 131, 149, 0.18)' : 'rgba(125, 26, 56, 0.1)',
                                  color: 'var(--blishka-berry)',
                                  fontWeight: 700,
                                  textTransform: 'uppercase'
                                }}
                              >
                                {status.replace('_', ' ')}
                              </span>
                            </div>

                            {/* Address & Direct Navigation */}
                            <div
                              style={{
                                background: '#fffdfb',
                                border: '1px solid var(--border-soft)',
                                borderRadius: '12px',
                                padding: '0.85rem 1rem',
                                marginBottom: '1rem'
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.88rem' }}>
                                <MapPin size={16} style={{ color: 'var(--blishka-berry)', flexShrink: 0, marginTop: '2px' }} />
                                <span style={{ color: 'var(--color-text-body)' }}>
                                  {b.address}, {b.area} {b.pincode ? `(${b.pincode})` : ''}
                                </span>
                              </div>

                              <div style={{ display: 'flex', gap: '0.65rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
                                <a
                                  href={mapsUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-outline btn-sm"
                                  style={{ flex: '1 1 140px', gap: '0.4rem', padding: '0.55rem', minHeight: '38px', justifyContent: 'center' }}
                                >
                                  <Navigation size={14} />
                                  <span>Open Google Maps</span>
                                </a>
                                <a
                                  href={`tel:${b.phone}`}
                                  className="btn btn-sm"
                                  style={{
                                    flex: '1 1 140px',
                                    gap: '0.4rem',
                                    padding: '0.55rem',
                                    minHeight: '38px',
                                    justifyContent: 'center',
                                    background: 'rgba(46, 125, 50, 0.1)',
                                    color: '#2e7d32',
                                    border: '1px solid rgba(46, 125, 50, 0.25)'
                                  }}
                                >
                                  <Phone size={14} />
                                  <span>Call (+91 {b.phone})</span>
                                </a>
                              </div>
                            </div>

                            {/* Doorstep Service Start OTP (Visible to Beautician) */}
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                background: 'linear-gradient(135deg, rgba(125, 26, 56, 0.05) 0%, rgba(216, 131, 149, 0.12) 100%)',
                                border: '1.5px dashed var(--blishka-berry)',
                                borderRadius: '10px',
                                padding: '0.65rem 0.95rem',
                                marginBottom: '1rem'
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <KeyRound size={17} style={{ color: 'var(--blishka-berry)', flexShrink: 0 }} />
                                <div>
                                  <span style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--blishka-berry)', fontWeight: 700, display: 'block' }}>
                                    Service Start OTP (Code):
                                  </span>
                                  <strong style={{ fontSize: '1.25rem', color: 'var(--blishka-berry)', letterSpacing: '3px', fontFamily: 'monospace' }}>
                                    {b.startOtp || '1234'}
                                  </strong>
                                </div>
                              </div>
                              <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', textAlign: 'right' }}>
                                Same code on customer screen
                              </span>
                            </div>

                            {/* Booked Services */}
                            <div style={{ fontSize: '0.86rem', marginBottom: '1rem' }}>
                              <span style={{ fontWeight: 600, color: 'var(--color-text-main)' }}>Services: </span>
                              <span style={{ color: 'var(--color-text-body)' }}>{b.items.map((i) => i.name).join(', ')}</span>
                            </div>

                            {/* Bill & Pay After Service */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px dashed var(--border-soft)', paddingTop: '0.75rem', marginBottom: '1.25rem' }}>
                              <div>
                                <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>Bill Amount: </span>
                                <strong style={{ fontSize: '1.15rem', color: 'var(--blishka-berry)' }}>₹{b.totalPrice}</strong>
                              </div>
                              <span style={{ fontSize: '0.8rem', color: '#2e7d32', fontWeight: 600 }}>
                                Pay After Service (Cash / UPI)
                              </span>
                            </div>

                            {/* Step Progression Buttons */}
                            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.75rem', width: '100%' }}>
                              {status === 'assigned' && (
                                <button
                                  type="button"
                                  onClick={() => handleTransit(b.bookingRef, 'in_transit')}
                                  className="btn btn-berry"
                                  style={{
                                    flex: 1,
                                    padding: '0.85rem 1rem',
                                    minHeight: '48px',
                                    fontSize: '0.96rem',
                                    fontWeight: 700,
                                    justifyContent: 'center',
                                    touchAction: 'manipulation',
                                    boxShadow: '0 3px 12px rgba(125, 26, 56, 0.22)'
                                  }}
                                >
                                  <span>I'm On the Way</span>
                                  <ChevronRight size={18} />
                                </button>
                              )}

                              {status === 'in_transit' && (
                                <button
                                  type="button"
                                  onClick={() => handleTransit(b.bookingRef, 'arrived')}
                                  className="btn"
                                  style={{
                                    flex: 1,
                                    padding: '0.85rem 1rem',
                                    minHeight: '48px',
                                    background: '#5c7c64',
                                    color: '#fff',
                                    fontSize: '0.96rem',
                                    fontWeight: 700,
                                    justifyContent: 'center',
                                    touchAction: 'manipulation',
                                    boxShadow: '0 3px 12px rgba(92, 124, 100, 0.22)'
                                  }}
                                >
                                  <span>Arrived at Doorstep</span>
                                  <CheckCircle2 size={18} />
                                </button>
                              )}

                              {status === 'arrived' && (
                                <button
                                  type="button"
                                  onClick={() => handleOpenOtpModal(b.bookingRef)}
                                  className="btn btn-berry"
                                  style={{
                                    flex: 1,
                                    padding: '0.85rem 1rem',
                                    minHeight: '48px',
                                    gap: '0.5rem',
                                    fontSize: '0.96rem',
                                    fontWeight: 700,
                                    justifyContent: 'center',
                                    touchAction: 'manipulation',
                                    boxShadow: '0 3px 12px rgba(125, 26, 56, 0.22)'
                                  }}
                                >
                                  <KeyRound size={18} />
                                  <span>Start Service (Enter Customer OTP)</span>
                                </button>
                              )}

                              {status === 'in_progress' && (
                                <button
                                  type="button"
                                  onClick={() => setPaymentTargetBooking(b)}
                                  className="btn"
                                  style={{
                                    flex: 1,
                                    padding: '0.85rem 1rem',
                                    minHeight: '48px',
                                    background: '#2e7d32',
                                    color: '#fff',
                                    fontSize: '0.96rem',
                                    fontWeight: 700,
                                    justifyContent: 'center',
                                    touchAction: 'manipulation',
                                    boxShadow: '0 3px 12px rgba(46, 125, 50, 0.22)'
                                  }}
                                >
                                  <CheckCircle2 size={18} />
                                  <span>Complete Service & Collect ₹{b.totalPrice}</span>
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}

                {/* ── TAB 2: AVAILABLE LEADS ── */}
                {activeTab === 'leads' && (
                  <div>
                    {availableLeads.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '3.5rem 1rem', color: 'var(--color-text-muted)' }}>
                        <AlertCircle size={44} style={{ margin: '0 auto 0.75rem', color: 'var(--blishka-rose)' }} />
                        <p style={{ margin: 0, fontWeight: 600, fontSize: '1.05rem' }}>No unassigned leads in queue.</p>
                        <p style={{ fontSize: '0.84rem', marginTop: '0.35rem' }}>
                          All incoming bookings are assigned. New customer bookings will appear here instantly!
                        </p>
                      </div>
                    ) : (
                      availableLeads.map((b) => (
                        <div
                          key={b.bookingRef}
                          className="portal-staff-card"
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                            <div>
                              <span style={{ fontSize: '0.74rem', color: 'var(--color-text-muted)' }}>Ref: {b.bookingRef}</span>
                              <h4 style={{ margin: '0.15rem 0', fontSize: '1.1rem', color: 'var(--color-text-main)' }}>
                                {b.clientName}
                              </h4>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--color-text-muted)', fontSize: '0.82rem' }}>
                                <MapPin size={13} />
                                <span>{b.area}</span>
                              </div>
                            </div>
                            <strong style={{ fontSize: '1.2rem', color: 'var(--blishka-berry)' }}>₹{b.totalPrice}</strong>
                          </div>

                          <div style={{ fontSize: '0.84rem', color: 'var(--color-text-body)', marginBottom: '0.5rem' }}>
                            <strong>Scheduled: </strong>{b.preferredDate} ({b.preferredSlot})
                          </div>

                          <div style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)', marginBottom: '1rem' }}>
                            {b.items.map((i) => i.name).join(', ')}
                          </div>

                          <button
                            type="button"
                            onClick={() => handleClaim(b.bookingRef)}
                            className="btn btn-berry"
                            style={{
                              width: '100%',
                              padding: '0.85rem 1rem',
                              minHeight: '48px',
                              fontSize: '0.96rem',
                              fontWeight: 700,
                              justifyContent: 'center',
                              touchAction: 'manipulation',
                              boxShadow: '0 3px 12px rgba(125, 26, 56, 0.22)'
                            }}
                          >
                            Claim This Booking (+1 to Monthly Goal)
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* ── TAB 3: COMPLETED VISITS ── */}
                {activeTab === 'completed' && (
                  <div>
                    {completedJobs.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '3.5rem 1rem', color: 'var(--color-text-muted)' }}>
                        <p style={{ margin: 0, fontWeight: 600 }}>No completed visits logged yet.</p>
                      </div>
                    ) : (
                      completedJobs.map((b) => (
                        <div
                          key={b.bookingRef}
                          className="portal-staff-card"
                          style={{ padding: '1.15rem', marginBottom: '0.85rem' }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
                            <strong style={{ fontSize: '0.95rem' }}>{b.clientName}</strong>
                            <span style={{ fontSize: '0.76rem', color: '#2e7d32', fontWeight: 700 }}>
                              {b.paymentStatus || 'Payment Collected'}
                            </span>
                          </div>
                          <div style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)', marginBottom: '0.35rem' }}>
                            Ref: {b.bookingRef} · {b.preferredDate} · ₹{b.totalPrice}
                          </div>
                          {b.customerRating && (
                            <div style={{ fontSize: '0.82rem', color: '#f59e0b', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                              <span>★ {b.customerRating} / 5 Stars</span>
                              {b.customerReview && (
                                <span style={{ color: 'var(--color-text-body)', fontStyle: 'italic', fontWeight: 400 }}>
                                  - "{b.customerReview}"
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

      {/* ── SUB-MODAL: ENTER CUSTOMER START OTP ── */}
      {otpTargetRef && (
        <div className="modal-overlay" style={{ zIndex: 1100000 }} onClick={() => setOtpTargetRef(null)}>
          <div
            className="booking-modal-card"
            style={{ maxWidth: '380px', padding: '1.75rem', textAlign: 'center', maxHeight: '90dvh', overflowY: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <KeyRound size={36} style={{ color: 'var(--blishka-berry)', margin: '0 auto 0.75rem' }} />
            <h4 style={{ margin: '0 0 0.35rem', color: 'var(--blishka-berry)' }}>
              Start Doorstep Service
            </h4>

            {(() => {
              const targetBooking = assignedJobs.find((j) => j.bookingRef === otpTargetRef);
              const code = targetBooking?.startOtp || '1234';
              return (
                <div style={{
                  background: 'rgba(125,26,56,0.06)',
                  border: '1.5px dashed var(--blishka-berry)',
                  borderRadius: '12px',
                  padding: '0.75rem',
                  margin: '0.75rem 0 1rem'
                }}>
                  <div style={{ fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--blishka-berry)', fontWeight: 700 }}>
                    Service Start OTP:
                  </div>
                  <div style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--blishka-berry)', letterSpacing: '5px', fontFamily: 'monospace' }}>
                    {code}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: '0.2rem' }}>
                    Same code is visible on customer's booking screen
                  </div>
                </div>
              );
            })()}

            <input
              type="text"
              maxLength={4}
              value={otpInputValue}
              onChange={(e) => {
                setOtpInputValue(e.target.value.replace(/\D/g, '').slice(0, 4));
                if (otpVerifyError) setOtpVerifyError('');
              }}
              placeholder="4-digit OTP"
              className="form-input"
              style={{
                width: '100%',
                textAlign: 'center',
                fontSize: '1.5rem',
                letterSpacing: '8px',
                fontWeight: 800,
                marginBottom: '1rem',
                padding: '0.75rem'
              }}
            />

            {otpVerifyError && (
              <div style={{ color: '#d32f2f', fontSize: '0.8rem', marginBottom: '1rem' }}>
                {otpVerifyError}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                type="button"
                onClick={() => setOtpTargetRef(null)}
                className="btn btn-outline"
                style={{ flex: 1, minHeight: '44px', touchAction: 'manipulation' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleVerifyOtp}
                disabled={otpInputValue.length !== 4}
                className="btn btn-berry"
                style={{ flex: 1, minHeight: '44px', touchAction: 'manipulation', fontWeight: 700 }}
              >
                Verify & Begin
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── SUB-MODAL: COMPLETE SERVICE & RECORD PAYMENT ── */}
      {paymentTargetBooking && (
        <div className="modal-overlay" style={{ zIndex: 1100000 }} onClick={() => setPaymentTargetBooking(null)}>
          <div
            className="booking-modal-card"
            style={{ maxWidth: '400px', padding: '1.75rem', textAlign: 'center', maxHeight: '90dvh', overflowY: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <CheckCircle2 size={40} style={{ color: '#2e7d32', margin: '0 auto 0.75rem' }} />
            <h4 style={{ margin: '0 0 0.35rem', color: 'var(--blishka-berry)' }}>
              Complete Service & Collect Payment
            </h4>
            <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', marginBottom: '1rem' }}>
              Total Bill Payable by {paymentTargetBooking.clientName}:
            </p>

            <div style={{ fontSize: '2.2rem', fontWeight: 800, color: 'var(--blishka-berry)', marginBottom: '1.25rem' }}>
              ₹{paymentTargetBooking.totalPrice}
            </div>

            {/* Payment Mode Selector */}
            <div style={{ textAlign: 'left', marginBottom: '1.5rem' }}>
              <label className="form-label" style={{ marginBottom: '0.5rem' }}>
                Select Mode of Payment Received:
              </label>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  type="button"
                  onClick={() => setSelectedPaymentMode('Cash')}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    borderRadius: '10px',
                    border: selectedPaymentMode === 'Cash' ? '2px solid var(--blishka-berry)' : '1px solid var(--border-soft)',
                    background: selectedPaymentMode === 'Cash' ? 'rgba(125,26,56,0.06)' : '#fff',
                    fontWeight: selectedPaymentMode === 'Cash' ? 700 : 500,
                    cursor: 'pointer',
                    minHeight: '44px',
                    touchAction: 'manipulation'
                  }}
                >
                  💵 Cash Received
                </button>

                <button
                  type="button"
                  onClick={() => setSelectedPaymentMode('UPI')}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    borderRadius: '10px',
                    border: selectedPaymentMode === 'UPI' ? '2px solid var(--blishka-berry)' : '1px solid var(--border-soft)',
                    background: selectedPaymentMode === 'UPI' ? 'rgba(125,26,56,0.06)' : '#fff',
                    fontWeight: selectedPaymentMode === 'UPI' ? 700 : 500,
                    cursor: 'pointer',
                    minHeight: '44px',
                    touchAction: 'manipulation'
                  }}
                >
                  📱 Direct UPI Scan
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                type="button"
                onClick={() => setPaymentTargetBooking(null)}
                className="btn btn-outline"
                style={{ flex: 1, minHeight: '44px', touchAction: 'manipulation' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmComplete}
                className="btn"
                style={{ flex: 1, background: '#2e7d32', color: '#fff', minHeight: '44px', touchAction: 'manipulation', fontWeight: 700 }}
              >
                Confirm Payment & Close
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Staff Install Guide Modal */}
      <StaffInstallGuideModal
        isOpen={showStaffInstallModal}
        onClose={() => setShowStaffInstallModal(false)}
      />
    </div>
  );
};
